const BASE_API_URL: string = (process.env.REACT_APP_API_URL || "").trim();

export interface RequestOptions {
    method: string;
    url: string;
    query?: Record<string, any>;
    body?: any;
    headers?: Record<string, string>;
    timeout?: number;
}

interface Response {
    ok: boolean;
    status: number;
    headers: Headers;
    json?: () => Promise<any>;
    arrayBuffer?: () => Promise<ArrayBuffer>;
}

interface ApiResponse {
    ok: boolean;
    status: number;
    body: any;
}

export default class ApiClient {
    private baseUrl: string;

    constructor() {
        this.baseUrl = BASE_API_URL;
    }

    async request(options: RequestOptions): Promise<ApiResponse> {
        let query: string = new URLSearchParams(options.query || {}).toString();
        if (query !== '') {
            query = '?' + query;
        }
        const timeout = options.timeout ?? 10000;

        let response: Response;
        try {
            const headers: Record<string, string> = {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                ...options.headers,
            };

            if (!(options.body instanceof FormData)) {
                headers['Content-Type'] = 'application/json';
                options.body = JSON.stringify(options.body);
            }
            const fetchOptions: RequestInit = {
                method: options.method,
                headers,
                body: options.body || null,
            };

            // Only set signal if timeout is specified
            if (options.timeout) {
                fetchOptions.signal = AbortSignal.timeout(timeout);
            }
            response = await fetch(this.baseUrl + options.url + query, fetchOptions);
            if (response.status === 401) {
                localStorage.removeItem('accessToken');
                const isExhibition = localStorage.getItem('exhibitionSession') !== null;
                localStorage.removeItem('exhibitionSession');
                window.location.href = isExhibition ? '/exhibition' : '/login';
                return {
                    ok: false,
                    status: 401,
                    body: null,
                };
            }
        } catch (error) {
            console.log(options);
            response = {
                ok: false,
                status: 500,
                headers: new Headers(), // Add empty Headers object to satisfy the interface
                json: async () => ({
                    code: 500,
                    message: 'The server is unresponsive',
                    description: error?.toString(),
                }),
            };
        }

        const contentType = response.headers.get('Content-Type');
        let body = null;
        if (response.status !== 204 && response.headers.get('content-length') !== '0') {
            if (!contentType?.includes('application/json') && response.arrayBuffer) {
                body = await response.arrayBuffer();
            } else if (contentType?.includes('application/json') && response.json) {
                body = await response.json()
            }
        }
        return {
            ok: response.ok,
            status: response.status,
            body: body,
        };
    }

    async get(url: string, query?: Record<string, any>, options?: RequestOptions): Promise<ApiResponse> {
        return this.request({ method: 'GET', url, query, ...(options || {}) });
    }

    async post(url: string, body: any, options?: RequestOptions): Promise<ApiResponse> {
        return this.request({ method: 'POST', url, body, ...(options || {}) });
    }

    async patch(url: string, body: any, options?: RequestOptions): Promise<ApiResponse> {
        return this.request({ method: 'PATCH', url, body, ...(options || {}) });
    }

    async put(url: string, body: any, options?: RequestOptions): Promise<ApiResponse> {
        return this.request({ method: 'PUT', url, body, ...(options || {}) });
    }

    async delete(url: string, options?: RequestOptions): Promise<ApiResponse> {
        return this.request({ method: 'DELETE', url, ...(options || {}) });
    }



    // Add additional methods if necessary.
}
